import {useZero} from '@/shared/integrations/zero/use-zero'
import {Button, ButtonGroup, useDialogContext, VStack} from '@chakra-ui/react'
import {DialogBody, DialogFooter, DialogHeader} from '@cypro/ui/components'
import {useAppForm} from '@cypro/ui/integrations/form'
import {validatePhone} from '@cypro/ui/schemas'
import {toaster} from '@cypro/ui/toaster'
import {revalidateLogic, useStore} from '@tanstack/react-form'
import {useTranslate} from '@tolgee/react'
import * as z from 'zod'

type EmergencyContact = {
  name?: string | null
  phone?: string | null
}

const EmergencyContactSchema = z.object({
  name: z.string().min(1),
  phone: validatePhone({required: true}),
})

export function EmergencyContactDialog({patientId, contact}: {patientId: string; contact?: EmergencyContact}) {
  const {t} = useTranslate('common')
  const zero = useZero()
  const context = useDialogContext()

  const form = useAppForm({
    defaultValues: {
      name: contact?.name || '',
      phone: contact?.phone || '',
    },
    validators: {
      onDynamic: EmergencyContactSchema,
    },
    validationLogic: revalidateLogic(),
    onSubmit({value}) {
      return zero.attemptMutation(
        zero.mutators.patients.updateEmergencyContact({
          id: patientId,
          name: value.name,
          phone: value.phone,
        }),
        async mutation => {
          const data = await mutation.server
          if (!data) return
          toaster.success({title: t('success'), description: t('patient_updated_successfully')})
          onClose()
        }
      )
    },
  })

  function onClose() {
    context.setOpen(false)
  }

  const isDefaultValue = useStore(form.store, state => state.isDefaultValue)

  return (
    <form
      onSubmit={e => {
        e.preventDefault()
        form.handleSubmit()
      }}
    >
      <form.AppForm>
        <DialogHeader>{t('emergency_contact')}</DialogHeader>

        <DialogBody minHeight="none">
          <VStack gap="5" width="100%">
            <form.AppField name="name">
              {field => <field.TextField label={t('name')} placeholder="e.g. Connor" />}
            </form.AppField>

            <form.AppField name="phone">
              {field => <field.PhoneField label={t('phone')} placeholder={t('phone_placeholder')} />}
            </form.AppField>
          </VStack>
        </DialogBody>

        <DialogFooter>
          <ButtonGroup size="md" colorPalette="cyan" width="100%" justifyContent="flex-end">
            <Button variant="outline" colorPalette="gray" onClick={onClose}>
              {t('cancel')}
            </Button>
            <form.SubmitButton disabled={isDefaultValue}>{t('save')}</form.SubmitButton>
          </ButtonGroup>
        </DialogFooter>
      </form.AppForm>
    </form>
  )
}
